import { FC } from 'react'
import { AppsLink } from '@wellesley/router'
import { AddressData } from '@wellesley/openapi'

const UNNAMED_ADDRESS = 'Unnamed address'

type AddressDisplayProps = {
  address: AddressData
  isCard?: boolean
}

const normalize = (value?: string | null) => value?.trim() || ''
const isNotSame = (left: string, right: string) =>
  left.toLowerCase() !== right.toLowerCase()

const getAddressLine = (address: AddressData) => {
  const name = normalize(address.name)
  const houseNumber = normalize(address.houseNumber)
  const street = normalize(address.street)
  const locality = normalize(address.locality) || normalize(address.subRegion)
  const region = normalize(address.region) || normalize(address.regionCode)
  const country =
    normalize(address.country) || normalize(address.countryCode).toUpperCase()
  const postalCode = normalize(address.postalCode)

  const streetLine = [street, houseNumber].filter(Boolean).join(' ')
  const localityLine = [postalCode, locality].filter(Boolean).join(' ')

  const parts = [
    isNotSame(streetLine, name) ? streetLine : '',
    localityLine,
    isNotSame(region, locality) ? region : '',
    country,
  ].filter(Boolean)

  return parts.join(', ')
}

export const AddressDisplay: FC<AddressDisplayProps> = ({
  address,
  isCard,
}) => {
  const formatted = normalize(address?.formatted)
  const name = normalize(address?.name)

  const hasCoordinates = !!(address?.latitude && address?.longitude)
  const resolvedAddress =
    (isCard ? name : getAddressLine(address)) || formatted || UNNAMED_ADDRESS

  const canLink = resolvedAddress !== UNNAMED_ADDRESS || hasCoordinates

  if (!canLink) {
    return <span>Unnamed address</span>
  }

  const to = hasCoordinates
    ? `https://www.google.com/maps/place/${address.latitude},${address.longitude}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(resolvedAddress)}`

  return (
    <AppsLink
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:underline"
    >
      {resolvedAddress}
    </AppsLink>
  )
}
